import { perfilVeiculoPorId } from '../didatico/perfilVeiculo'
import { localParaMundo, mundoParaLocal, posoDoObjeto, type PosoVeiculoMundo } from './referencial'
import type { CenaTabletop, ObjetoTabletop, TipoObjetoTabletop, TransformacaoTabletop } from './tipos'

/**
 * Tipos de objeto tático/risco/vítima que podem ser apoiados sobre a superfície
 * da lataria de um veículo (capô, teto, para-brisa, etc.).
 *
 * Equipamentos de estabilização (calços, cunhas, blocos, v-strut) são intencionalmente
 * excluídos desta lista para que continuem perfurando até o solo (sob a soleira).
 */
export const TIPOS_APOIAVEIS_EM_VEICULO: readonly TipoObjetoTabletop[] = [
  'fogo',
  'derramamento',
  'caboEletrico',
  'extintorPQS',
  'mochilaTrauma',
  'vitima',
]

/** Verifica se um tipo de objeto pode repousar sobre a lataria do veículo. */
export function podeApoiarEmVeiculo(tipo: TipoObjetoTabletop): boolean {
  return (TIPOS_APOIAVEIS_EM_VEICULO as readonly string[]).includes(tipo)
}

/**
 * Determina se um objeto está atualmente apoiado sobre a lataria de um determinado veículo.
 *
 * Critérios acordados no grill:
 * 1. O tipo do objeto está no escopo de apoio (`podeApoiarEmVeiculo`);
 * 2. Não é uma vítima já sentada dentro do veículo (que tem sua própria ancoragem de banco);
 * 3. Sua altura Y está elevada em relação ao solo (Y > 0.05 m);
 * 4. Sua coordenada horizontal (X, Z) está contida na projeção/pegada da carroceria do veículo.
 */
export function objetoApoiadoNoVeiculo(objeto: ObjetoTabletop, veiculo: ObjetoTabletop): boolean {
  if (objeto.id === veiculo.id) return false
  if (!objeto || !veiculo?.veiculo) return false
  if (!podeApoiarEmVeiculo(objeto.tipo)) return false
  if (objeto.vitima?.local.tipo === 'veiculo') return false

  // Se o objeto estiver no chão (Y <= 0.05 m), está sobre o asfalto, não no capô
  if (objeto.transformacao.posicao[1] <= 0.05) return false

  const poso = posoDoObjeto(veiculo)
  const local = mundoParaLocal(
    { x: objeto.transformacao.posicao[0], z: objeto.transformacao.posicao[2] },
    poso,
  )

  const perfil = perfilVeiculoPorId(veiculo.veiculo.idPerfil)
  const margemM = 0.35 // tolerância para bordas do capô/para-choque
  const meiaLargura = (perfil?.largura ?? 1.9) / 2 + margemM
  const meioComprimento = (perfil?.comprimento ?? 4.5) / 2 + margemM

  return Math.abs(local.x) <= meiaLargura && Math.abs(local.z) <= meioComprimento
}

/**
 * Calcula o novo posicionamento e orientação de um objeto apoiado quando o veículo
 * de suporte sofre uma transformação (translação, rotação ou escala).
 */
export function calcularTransformacaoApoiada(
  objeto: ObjetoTabletop,
  posoAntigo: PosoVeiculoMundo,
  posoNovo: PosoVeiculoMundo,
  deltaY = 0,
): TransformacaoTabletop {
  const rotAntiga = objeto.transformacao.rotacao ?? [0, 0, 0]
  const posAntiga = objeto.transformacao.posicao ?? [0, 0, 0]
  const escAntiga = objeto.transformacao.escala ?? [1, 1, 1]

  const local = mundoParaLocal(
    { x: posAntiga[0], z: posAntiga[2] },
    posoAntigo,
  )
  const novoPontoMundo = localParaMundo(local, posoNovo)
  const deltaGuinada = posoNovo.guinadaRad - posoAntigo.guinadaRad
  const novaRotacaoY = rotAntiga[1] + deltaGuinada

  return {
    posicao: [
      novoPontoMundo.x,
      Math.max(0, posAntiga[1] + deltaY),
      novoPontoMundo.z,
    ],
    rotacao: [
      rotAntiga[0],
      novaRotacaoY,
      rotAntiga[2],
    ],
    escala: [...escAntiga],
  }
}

/**
 * Move ou rotaciona um veículo e arrasta solidariamente todos os objetos apoiados em sua lataria.
 */
export function moverVeiculoComApoiados(
  cena: CenaTabletop,
  idVeiculo: string,
  alteracoes: Partial<TransformacaoTabletop>,
): CenaTabletop {
  const veiculo = cena.objetos.find((o) => o.id === idVeiculo)
  if (!veiculo?.veiculo) return cena

  const posoAntigo = posoDoObjeto(veiculo)

  const novaTransformacaoVeiculo: TransformacaoTabletop = {
    posicao: alteracoes.posicao
      ? [...alteracoes.posicao]
      : [...(veiculo.transformacao.posicao ?? [0, 0, 0])],
    rotacao: alteracoes.rotacao
      ? [...alteracoes.rotacao]
      : [...(veiculo.transformacao.rotacao ?? [0, 0, 0])],
    escala: alteracoes.escala
      ? [...alteracoes.escala]
      : [...(veiculo.transformacao.escala ?? [1, 1, 1])],
  }

  const posoNovo: PosoVeiculoMundo = {
    x: novaTransformacaoVeiculo.posicao[0],
    z: novaTransformacaoVeiculo.posicao[2],
    guinadaRad: novaTransformacaoVeiculo.rotacao[1],
    espelharX: veiculo.veiculo.posicaoFinal === 'tombadoDireito',
  }

  const deltaY = novaTransformacaoVeiculo.posicao[1] - (veiculo.transformacao.posicao?.[1] ?? 0)

  const apoiadosIds = new Set(
    cena.objetos.filter((o) => objetoApoiadoNoVeiculo(o, veiculo)).map((o) => o.id),
  )

  return {
    ...cena,
    objetos: cena.objetos.map((objeto) => {
      if (objeto.id === idVeiculo) {
        return {
          ...objeto,
          transformacao: novaTransformacaoVeiculo,
        }
      }
      if (apoiadosIds.has(objeto.id)) {
        return {
          ...objeto,
          transformacao: calcularTransformacaoApoiada(objeto, posoAntigo, posoNovo, deltaY),
        }
      }
      return objeto
    }),
  }
}

